import React, { useEffect, useState } from "react";
import CustomModal from "../../components/common/Modal";
import InputField from "../../components/Input/Inputfeild";
import RecipientSelect from "./RecipientSelect";
import PhoneRecipientSelect from "./PhoneRecipientSelect";
import { FILE_TYPE_VALUES, SCHEDULE_TYPE_VALUES, isPhoneTask, matchOptionValue } from "./scheduleMappers";
import "./ScheduleLogs.css";

/**
 * Send a scheduled report to someone else — the share icon on a Schedule Logs row.
 *
 * Was a whole page at /share-schedule-report (ShareReportForm); the new design makes it a
 * dialog over the log it belongs to, which is also what lets the title name the run.
 *
 * ── THE FIELDS NOW MATCH CREATE SCHEDULE ────────────────────────────────────
 * Share with switches on the delivery type, exactly as Send To does on the create page:
 * an address field for Email, name-and-number entry for WhatsApp and SMS. It used to be
 * an email picker whichever channel was chosen, so picking WhatsApp offered a field that
 * could only collect the one thing WhatsApp cannot use.
 *
 * Publisher is a plain text box for the same reason it is one on the create page: there
 * is no user API to pick from, and it is stored as a plain string. It used to be a
 * PublisherSelect fed from data/users.json - invented people, in a dialog that sends real
 * mail.
 *
 * ── NOTHING IS SENT YET ─────────────────────────────────────────────────────
 * There is no endpoint behind Share. Generating the file is solved - POST
 * /generate-report does it - but nothing in either service can deliver a message: our API
 * has no mail library and no SMTP settings, and the scheduler's Re-Send is unimplemented.
 * Raised with the scheduler team on 2026-09-14. See the Share button's title.
 */

/* The five the endpoint actually produces, and the same list the create page offers -
   shared rather than repeated, because this copy had drifted to the pre-/generate-report
   set: it still offered JPEG and Link, neither of which exists, and omitted PNG. */
const FILE_TYPES = FILE_TYPE_VALUES;

const ChannelIcon = {
  Email: (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none" aria-hidden="true">
      <rect x="1.5" y="3.5" width="13" height="9" rx="1.8" stroke="currentColor" strokeWidth="1.4" />
      <path d="M2 4.6l6 4 6-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  WhatsApp: (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="6.4" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M5.9 5.6c.3 0 .4.6.6.9.1.2-.3.5-.3.7 0 .3 1 1.5 1.7 1.7.2 0 .5-.4.7-.3.3.1.9.3.9.6 0 .5-.5.9-1 .9-1.4 0-3.4-2-3.4-3.4 0-.5.4-1.1.8-1.1z"
        fill="currentColor"
      />
    </svg>
  ),
  SMS: (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none" aria-hidden="true">
      <path
        d="M14 9.5a2 2 0 01-2 2H6l-3.5 2.5v-2.5a2 2 0 01-.5-1.3V4.5a2 2 0 012-2h8a2 2 0 012 2v5z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  ),
};

const CHANNELS = SCHEDULE_TYPE_VALUES;

const emptyShare = () => ({
  deliveryType: "Email",
  publisher: "",
  recipients: [],
  fileType: "PDF",
});

/* No endpoint behind the button yet - see the note at the top of the file. */
const SHARE_UNAVAILABLE = "Sharing is not available yet — there is no endpoint to send the report.";

export default function ShareReportModal({ open, log, onClose, onShare }) {
  const [form, setForm] = useState(emptyShare);

  /* Reset each time it opens, and seed File Type from the run being shared — the same
     format it was generated in is the sensible default.

     Through matchOptionValue because the log carries what the API stored, which is
     lowercase ("pdf"), while the select's values are the display forms ("PDF"). Used raw
     it matched no option and the field opened blank. */
  useEffect(() => {
    if (!open) return;
    setForm({
      ...emptyShare(),
      fileType: matchOptionValue(FILE_TYPES, log?.fileType) || "PDF",
    });
  }, [open, log]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  /* Changing the channel clears the recipients. An address carries no number and a number
     carries no address, so whichever way it goes the existing entries would be dropped —
     emptying the field visibly is better than sharing with nobody. Same reasoning, and
     the same test, as the create page's Schedule Type select. */
  const setChannel = (channel) =>
    set(
      isPhoneTask(channel) === isPhoneTask(form.deliveryType)
        ? { deliveryType: channel }
        : { deliveryType: channel, recipients: [] },
    );

  const isPhone = isPhoneTask(form.deliveryType);

  /* `const canShare = form.recipients.length > 0` went with the working button. It comes
     back, along with `disabled={!canShare}`, the day there is an endpoint to call. */

  /* `taskDate` — the run being shared. It was `scheduledDate`, which turned out to hold the
     schedule's *next* due time, so this dialog offered to share a run that had not happened
     yet. Renamed in rowFromScheduleLog on 2026-09-12. */
  const title = log ? `Share ${log.reportName} for ${log.taskDate}` : "Share report";

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      title={title}
      size="medium"
      footer={
        <div className="slg-share-footer">
          <button type="button" className="slg-btn slg-btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="slg-btn slg-btn-primary"
            /* Disabled outright while there is nothing to send with. A button that
               accepted the click and did nothing would read as a successful share —
               the same trap the Re-Send button had. */
            disabled
            title={SHARE_UNAVAILABLE}
            onClick={() => onShare?.(form)}
          >
            Share
          </button>
        </div>
      }
    >
      <div className="slg-share-field">
        <span className="slg-share-label">Delivery Type</span>
        <div className="slg-delivery-types">
          {CHANNELS.map((channel) => (
            <label className="slg-delivery-type" key={channel}>
              <input
                type="radio"
                name="slg-delivery-type"
                checked={form.deliveryType === channel}
                onChange={() => setChannel(channel)}
              />
              <span className={`slg-channel-icon slg-channel-${channel.toLowerCase()}`}>{ChannelIcon[channel]}</span>
              {channel}
            </label>
          ))}
        </div>
      </div>

      {/* Free text, not a picker — the same as the create page's Publisher, and for the
          same reason: there is no user API to choose from and it is stored as a string. */}
      <div className="slg-share-field">
        <span className="slg-share-label">Publisher</span>
        <InputField
          type="text"
          placeholder="Publisher name..."
          value={form.publisher}
          onChange={(e) => set({ publisher: e.target.value })}
        />
      </div>

      {/* The control follows the channel, exactly as Send To does on the create page.
          RecipientSelect gets no `users` prop, which drops its dropdown and leaves plain
          email entry — pass a list again when there is a real one. */}
      <div className="slg-share-field">
        <span className="slg-share-label">Share with</span>
        {isPhone ? (
          <PhoneRecipientSelect value={form.recipients} onChange={(r) => set({ recipients: r })} />
        ) : (
          <RecipientSelect value={form.recipients} onChange={(r) => set({ recipients: r })} />
        )}
      </div>

      <div className="slg-share-hint">
        {isPhone
          ? "Enter a name and number, then press Add."
          : "Type an address and press Enter, or separate several with commas."}
      </div>

      <div className="slg-share-field">
        <span className="slg-share-label">File Type</span>
        <select className="slg-share-select" value={form.fileType} onChange={(e) => set({ fileType: e.target.value })}>
          {FILE_TYPES.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>
      </div>
    </CustomModal>
  );
}
